import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for HR Management) ---
import {
    // Core Concepts & General
    faUsers,            // People / Workforce / Engagement / Team
    faUserCog,          // HR Management / Staffing / Skills
    faHospitalUser,     // Patient Care Focus
    faBuilding,         // Facility / Organization
    faCheckCircle,      // Compliance / Quality / Success
    // Why HRM is Critical (Benefits)
    faUserGraduate,     // Skilled Workforce
    faDollarSign,       // Cost Control / Compensation
    faHeart,            // Employee Engagement / Retention / Motivation / Culture
    faBalanceScale,     // Compliance / Regulations
    faChalkboardTeacher,// Training & Development
    // Cliniqor's Services (8 Points)
    faCalendarAlt,      // 1. HR Planning
    faUserPlus,         // 2. Recruitment
    faFileAlt,          // 3. HR Policies & Systems
    faChartBar,         // 4. Performance Management
    faSmileBeam,        // 5. Motivation & Work Culture
    // faChalkboardTeacher reused for 6. Training & Development
    faAward,            // 7. Reward Management
    faMoneyBillWave,    // 8. Compensation Systems
    // Why Choose Cliniqor
    faUserTie,          // Expertise
    faCog,              // Custom Solutions (Tailored)
    faTasks,            // Comprehensive Services
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
     faHandshake, faClipboardCheck, faNetworkWired, faTasks, faChartLine, faBullhorn, faLightbulb,
     faMapSigns, faBullseye, faSitemap, faCalculator, faLaptopCode, faGavel, faHandHoldingDollar,
     faExpandArrowsAlt, faTrophy, faThumbsUp, faMedal, faSearchLocation, faBoxOpen, faComments,
     faTags, faStar, faPrint, faMousePointer, faNewspaper, faCalendarCheck, faCrosshairs
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Critical?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor's Services
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Choose Cliniqor?

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for HR/teamwork
    const heroBgImage = 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image showing team/collaboration

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Human Resource Management for Healthcare Facilities</h1>
            <p className="cliniqor-sub-heading">
              Building a Strong Foundation for Healthcare Success with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Effective HRM is essential for ensuring the right people deliver high-quality care. Cliniqor offers end-to-end HR services covering staffing, performance, training, engagement, and culture development for healthcare excellence.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- WHY HRM IS CRITICAL --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Why is Human Resource Management Critical in Healthcare?</h2>
              <p>More than hiring—creating an environment for professionals to thrive.</p>
            </div>
             {/* Using Features Grid for the 5 critical points */}
            <div className="cliniqor-features-grid">
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserGraduate} /></div>
                        <h4>1. Skilled Workforce for Optimal Operations</h4>
                        <p>Ensuring staff have the right qualifications, skills, and experience for quality care.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                        <h4>2. Cost Control & Staffing Efficiency</h4>
                        <p>Optimizing staffing levels to avoid shortages/surpluses and reduce recruitment costs.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHeart} /></div>
                        <h4>3. Employee Engagement & Retention</h4>
                        <p>Creating a positive environment to value staff, reduce turnover, and retain top talent.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBalanceScale} /></div>
                        <h4>4. Compliance with Healthcare Regulations</h4>
                        <p>Ensuring staff meet licensing/credentialing requirements and minimizing legal risks.</p>
                    </div>
                </div>
                 <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                        <h4>5. Workforce Development & Training</h4>
                        <p>Keeping staff updated with latest advancements, tech, and best practices.</p>
                    </div>
                </div>
            </div>
          </div>
        </section>

        {/* --- CLINIQOR'S SERVICES (Detailed Planning Section - 8 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>How Cliniqor Enhances HR Management in Healthcare</h2>
               <p>Customized, comprehensive HR solutions for exceptional care and efficiency.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 8 Service Areas */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCalendarAlt} /></div>
                    <div className="cliniqor-content"><h4>1. Human Resource Planning</h4> <p>Forecasting needs, aligning talent with vision, demand forecasting, budget alignment, skill mapping.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUserPlus} /></div>
                    <div className="cliniqor-content"><h4>2. Recruitment</h4> <p>Sourcing top talent, comprehensive screening, temporary/permanent staffing for all roles.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faFileAlt} /></div>
                    <div className="cliniqor-content"><h4>3. HR Policies & Systems</h4> <p>Custom policy development (ethics, conduct, confidentiality), performance systems, handbooks.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChartBar} /></div>
                    <div className="cliniqor-content"><h4>4. Performance Management</h4> <p>Setting KPIs, regular appraisals, feedback, Individual Development Plans (IDPs), 360-degree feedback.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSmileBeam} /></div> {/* Culture */}
                    <div className="cliniqor-content"><h4>5. Motivation & Work Culture</h4> <p>Fostering well-being, recognition/rewards, team-building, leadership development for engagement.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                    <div className="cliniqor-content"><h4>6. Training & Development</h4> <p>Induction, specialized skills (tech, care protocols), leadership training for future needs.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faAward} /></div> {/* Rewards */}
                    <div className="cliniqor-content"><h4>7. Reward Management</h4> <p>Implementing incentive schemes (financial/non-financial), recognition, benefits for loyalty.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMoneyBillWave} /></div>
                    <div className="cliniqor-content"><h4>8. Compensation Systems</h4> <p>Designing fair, competitive salary structures, bonuses, and benefit packages.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- WHY CHOOSE CLINIQOR? (Reusing Features Layout - 3 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Why Choose Cliniqor for Your HR Management Needs?</h2>
                 <p>Deep understanding of healthcare complexities and tailored solutions.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Why Choose --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserTie} /></div>
                            <h4>Deep Healthcare Expertise</h4>
                            <p>Years of experience understanding unique HR challenges in healthcare.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCog} /></div>
                            <h4>Customized HR Solutions</h4>
                            <p>Tailored approaches for recruitment, policies, performance, and training.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div> {/* Comprehensive */}
                            <h4>Comprehensive Support</h4>
                            <p>End-to-end services creating supportive, efficient, compliant environments.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h2>Empowering Healthcare Excellence Through Effective Human Resource Management</h2>
          <p>Partner with Cliniqor to build a strong, motivated, and skilled workforce that drives quality patient care and operational success. Contact us today.</p>
          <a href="#contact" className="cliniqor-cta-button">Discuss Your HR Needs</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;